import {
  type SendData,
  type CollabStateBridge,
} from '@atlassian/collab-connection-client';
import { uuidv4 } from 'lib0/random.js';

import { encodeMessageV2 } from '@canvas/protocol/v2';
import { MessageType } from '@canvas/protocol-types';
import { getUpdateMessageOrigin } from '@canvas/transaction';

import { mergeUpdate } from '../util/merge-update';

import { type Update, type AbstractBatchedUpdates } from './abstract-batched-updates';
import { type UpdateMetadata } from './yjs-socket';

export class StateBridge
  implements
    CollabStateBridge<Uint8Array>,
    AbstractBatchedUpdates<Uint8Array<ArrayBuffer>, UpdateMetadata>
{
  // stores all pending changes
  private buffer: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>[] = [];
  // stores the current pending outgoing updates
  private outgoing = 0;
  private outgoingId?: string;
  private observer?: (data: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>) => void;
  private flushObserver?: () => void;
  private confirmObserver?: (sentAt: number, receivedAt: number) => void;
  private mismatchObserver?: () => void;
  private lastSentClock: number | null = null;
  private retrievedAt: number | null = null;

  addUpdate(update: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>): void {
    this.buffer.push(update);
    // calling observer here eventually triggers sendThenConfirm();
    this.observer?.(update);
  }

  flush() {
    if (this.buffer.length > 0) {
      this.flushObserver?.();
    }
  }

  // this will set observer to be a function that eventually calls c3.sendThenConfirm (through the abstract Transport layer)
  onUpdate(
    observer: (data: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>) => void
  ): this {
    this.observer = observer;
    return this;
  }

  onFlush(flushObserver: () => void): this {
    this.flushObserver = flushObserver;
    return this;
  }

  // no-op, timer is managed by C3, not by state bridge
  destroy() {}

  reset() {
    this.buffer = [];
    this.outgoing = 0;
    this.outgoingId = undefined;
    this.lastSentClock = null;
    this.retrievedAt = null;
  }

  retrieve(): SendData<Uint8Array> | null {
    // return undefined payload if empty buffer
    if (this.buffer.length < 1) {
      return null;
    }
    const updatesToSend = this.buffer.map((update) => ({
      ...update,
      payload: structuredClone(update.payload),
    }));
    this.outgoing = updatesToSend.length;
    this.outgoingId = uuidv4();
    const mergedUpdate = mergeUpdate(updatesToSend);
    const updateToSend = {
      name: 'update',
      payload: encodeMessageV2({
        type: MessageType.Update,
        data: {
          data: mergedUpdate.payload,
          origin: getUpdateMessageOrigin(mergedUpdate.origin),
          previousYjsClock: this.lastSentClock,
          currentYjsClock: mergedUpdate.clock,
        },
      }),
      id: this.outgoingId,
    };
    this.lastSentClock = mergedUpdate.clock;
    this.retrievedAt = Date.now();
    return updateToSend;
  }

  confirm(_confirmed: SendData<Uint8Array>, id?: string): Promise<void> {
    // check the id to make sure it matches
    if (id !== undefined && id === this.outgoingId) {
      this.retrievedAt && this.confirmObserver?.(this.retrievedAt, Date.now());
      // remove the first N elements from the buffer, where N is the number of updates batched in the latest send
      this.buffer = this.buffer.slice(this.outgoing);
      this.outgoing = 0;
    } else if (id !== undefined) {
      // id was provided but doesn't match — signal that state is out of sync
      this.mismatchObserver?.();
    }
    return Promise.resolve();
  }

  onConfirm(observer: (sentAt: number, receivedAt: number) => void): this {
    this.confirmObserver = observer;
    return this;
  }

  onMismatch(observer: () => void): this {
    this.mismatchObserver = observer;
    return this;
  }
}
